<script setup>
import { ref, computed, onBeforeUnmount, watch } from 'vue'
import {debounce} from 'lodash'
import { useStore } from 'vuex'
import { area, length } from '@turf/turf'
import {bbox} from '@turf/turf'
import { isClosedLineString, closedLineStringToPolygon, autoSimplifyFeature, hasAreaGeometry, mergePolygons } from '@/utils/geometry'

// Safe fitBounds that validates bbox before calling map.fitBounds
const safeFitBounds = (mapInstance, geometry, options = { padding: 50, duration: 1000 }) => {
  try {
    const bounds = bbox(geometry)
    // Validate bounds: [minLng, minLat, maxLng, maxLat]
    const [minLng, minLat, maxLng, maxLat] = bounds
    if (
      minLat < -90 || minLat > 90 ||
      maxLat < -90 || maxLat > 90 ||
      minLng < -180 || minLng > 180 ||
      maxLng < -180 || maxLng > 180
    ) {
      console.warn('Invalid bounds detected, skipping fitBounds:', bounds)
      return
    }
    mapInstance.fitBounds(bounds, options)
  } catch (e) {
    console.warn('fitBounds failed:', e.message)
  }
}

import { Spinner } from '@/components/ui/spinner'
import {X, FilePlus2, Brush, MapPin, Pencil, Hexagon, Square, Circle} from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {parseImportFile, partitionImportFiles, ImportError, IMPORT_ACCEPT} from '@/composables/files'
import { searchPlaces } from '@/utils/nominatim'
import FeatureSelectionItem from './FeatureSelectionItem.vue'

const drawModeMap = {
  point: { label: 'Point', mode: 'draw_point', icon: MapPin },
  line: { label: 'Line', mode: 'draw_line_string', icon: Pencil },
  polygon: { label: 'Polygon', mode: 'draw_polygon', icon: Hexagon },
  circle: { label: 'Circle', mode: 'draw_circle', icon: Circle },
}

const props = defineProps({
  selectedGeometry: {
    type: Object,
    default: null
  },
  selectedGeometries: {
    type: Array,
    default: () => []
  },
  multiSelect: {
    type: Boolean,
    default: false
  },
  color: {
    type: String,
    default: '#0059ff'
  },
  allowedDrawModes: {
    type: Array,
    default: () => ['point', 'line', 'polygon']
  },
  defaultPreview: {
    type: Boolean,
    default: false
  },
  // Single mode: an opened file becomes one feature holding all of its polygons, not one per shape.
  mergeFile: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:selectedGeometry', 'update:selectedGeometries', 'select'])

const store = useStore()
const isMapReady = computed(() => store.getters['geo/getMapReady'])
const map = computed(() => store.getters['geo/getMap'])
const draw = computed(() => store.getters['geo/getDraw'])

const previewGeometry = ref(null)
const isDrawing = ref(false)
const drawPopoverOpen = ref(false)
const isSearching = ref(false)
const searchText = ref('')
const searchFeatures = ref([])
const featureFileInput = ref(null)

// Counter for generating unique IDs
let idCounter = 0

// Generate unique ID with better fallback than Date.now() + Math.random()
const generateUniqueId = (prefix = 'feature') => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`
  }
  // Fallback: use counter for uniqueness
  idCounter++
  return `${prefix}-${Date.now()}-${idCounter}`
}

const resetSearch = () => {
  const selected = props.selectedGeometry;
  searchFeatures.value = [];

  if (!selected) {
    emit('update:selectedGeometry', null);
  } else if (!searchFeatures.value.includes(selected)) {
    searchFeatures.value.push(selected);
  }
  previewGeometry.value = null;
}

const searchLocation = async (value) => {
  searchFeatures.value = [];

  // Preserve selected geometries in multi-select mode
  if (props.multiSelect) {
    for (const geom of props.selectedGeometries) {
      searchFeatures.value.push(geom);
    }
  } else if (props.selectedGeometry) {
    searchFeatures.value.push(props.selectedGeometry);
  }

  const results = await searchPlaces(value, { limit: 10 })

  const allowsOnlyPoints = props.allowedDrawModes.length === 1 && props.allowedDrawModes[0] === 'point'
  for (const result of results) {
    if (!allowsOnlyPoints && (result.geojson.type === 'Point' || result.geojson.type === 'LineString')) {
      continue
    }
    const feature = autoSimplifyFeature({
      type: 'Feature',
      id: result.place_id ? `search-${result.place_id}` : generateUniqueId('search'),
      geometry: result.geojson,
      properties: {
        isSearch: true,
        name: result.display_name || result.name,
        addresstype: result.addresstype,
        placeId: result.place_id,
        osmType: result.osm_type,
        osmId: result.osm_id,
      }
    })

    searchFeatures.value.push(feature)
  }
}

const showFeature = (feature) => {
  previewGeometry.value = feature
}

const search = debounce(async () => {
  isSearching.value = true
  await searchLocation(searchText.value)
  isSearching.value = false
}, 1000)

const handleFileInputClick = () => {
  featureFileInput.value.click()
}

const handleFile = async (e) => {
  const files = e.target.files
  if (!files) return
  if (files.length === 0) return

  const newFeatures = []

  const { bundles, others } = partitionImportFiles(files)
  for (const unit of [...bundles.values(), ...others]) {
    const unitName = unit.name || unit.fileName
    let layers
    try {
      ({ layers } = await parseImportFile(unit))
    } catch (e) {
      toast({
        title: 'Could not import file',
        description: e instanceof ImportError ? e.message : unitName,
        variant: 'destructive'
      })
      continue
    }

    const unitFeatures = layers.flatMap(l => l.collection.features).filter(f => f.geometry)
    const merged = props.mergeFile && !props.multiSelect && unitFeatures.length > 1 ? mergePolygons(unitFeatures) : null
    const features = merged
      ? [{ type: 'Feature', geometry: merged, properties: { name: unitName.replace(/\.[^.]+$/, '') } }]
      : unitFeatures

    for (let feature of features) {
      feature.properties.isFromFile = true
      feature.properties.fileName = unitName
      if (!feature.id) {
        feature.id = generateUniqueId('feature')
      }
      feature = autoSimplifyFeature(feature)
      searchFeatures.value.push(feature)
      newFeatures.push(feature)
    }
  }

  if (newFeatures.length === 0) {
    toast({
      title: 'No features found',
      description: 'The uploaded file(s) contain no valid features',
      variant: 'destructive'
    })
    return
  }

  // In multi-select mode, auto-select first feature if nothing is selected
  if (props.multiSelect) {
    if (props.selectedGeometries.length === 0 && newFeatures.length > 0) {
      emit('update:selectedGeometries', [newFeatures[0]])
    }
  } else {
    emit('update:selectedGeometry', newFeatures[0])
    emit('select', newFeatures[0])
  }
}

const resetFiles = () => {
  if (featureFileInput.value) {
    featureFileInput.value.value = ''
  }
}

const selectGeometry = async (feature) => {
  if (!feature) {
    emit('update:selectedGeometry', null)
    return
  }
  emit('update:selectedGeometry', feature)
  emit('select', feature)

  if (feature.geometry) {
    safeFitBounds(map.value, feature.geometry)
  }
}

const toggleGeometrySelection = (feature) => {
  if (!props.multiSelect) {
    emit('update:selectedGeometry', feature ? feature : null)
    emit('select', feature)
    if (feature && feature.geometry) {
      safeFitBounds(map.value, feature.geometry)
    }
    return
  }

  const current = [...props.selectedGeometries]
  const idx = current.findIndex(f => f.id === feature.id)

  if (idx >= 0) {
    current.splice(idx, 1)
  } else {
    current.push(feature)
    // Fit bounds to newly selected feature
    if (feature.geometry) {
      safeFitBounds(map.value, feature.geometry)
    }
  }

  emit('update:selectedGeometries', current)
}

const startDrawing = (mode) => {
  if (!draw.value) {
    toast({
      title: 'Map is not ready',
      description: 'Please wait for the map to load and try again',
      variant: 'destructive'
    })
    return
  }
  draw.value.changeMode(mode)
  isDrawing.value = true
}

const stopDrawing = () => {
  if (draw.value) {
    draw.value.changeMode('simple_select')
  }
  isDrawing.value = false
}

const onDrawCreate = (e) => {
  isDrawing.value = false
  const features = e.features
  for (const feature of features) {
    let areaText = ''

    if (hasAreaGeometry(feature.geometry)) {
      const areaInSm = area(feature)
      const areaInHa = (areaInSm / 10000).toFixed(3) // 1 ha = 10000 sm
      const areaInSkm = (areaInSm / 1000000).toFixed(4) // 1 skm = 1000000 sm
      areaText = `${areaInHa} ha (${areaInSkm} km²)`
    } else if (feature.geometry.type === 'LineString') {
      // Check if it's a closed LineString (circle/buffer)
      if (isClosedLineString(feature.geometry)) {
        const polygonGeom = closedLineStringToPolygon(feature.geometry)
        const areaInSm = area({ type: 'Feature', geometry: polygonGeom })
        const areaInHa = (areaInSm / 10000).toFixed(3)
        const areaInSkm = (areaInSm / 1000000).toFixed(4)
        areaText = `${areaInHa} ha (${areaInSkm} km²)`
      } else {
        const lengthInM = length(feature, { units: 'meters' })
        const lengthInKm = (lengthInM / 1000).toFixed(2)
        areaText = `${lengthInKm} km`
      }
    }

    feature.properties = {
      ...feature.properties,
      isDrawn: true,
      areaText
    }
    // Ensure feature has ID
    if (!feature.id) {
      feature.id = generateUniqueId('drawn')
    }
    searchFeatures.value.push(feature)
  }
  if (features.length > 0) {
    if (props.multiSelect) {
      // In multi-select mode, add to selected geometries
      const current = [...props.selectedGeometries]
      current.push(features[0])
      emit('update:selectedGeometries', current)
    } else {
      emit('update:selectedGeometry', features[0])
      emit('select', features[0])
    }
  }
}

const onDrawUpdate = (e) => {
  const features = e.features
  for (const feature of features) {
    let areaText = ''

    if (hasAreaGeometry(feature.geometry)) {
      const areaInSm = area(feature)
      const areaInHa = (areaInSm / 10000).toFixed(3)
      const areaInSkm = (areaInSm / 1000000).toFixed(4)
      areaText = `${areaInHa} ha (${areaInSkm} km²)`
    } else if (feature.geometry.type === 'LineString') {
      // Check if it's a closed LineString (circle/buffer)
      if (isClosedLineString(feature.geometry)) {
        const polygonGeom = closedLineStringToPolygon(feature.geometry)
        const areaInSm = area({ type: 'Feature', geometry: polygonGeom })
        const areaInHa = (areaInSm / 10000).toFixed(3)
        const areaInSkm = (areaInSm / 1000000).toFixed(4)
        areaText = `${areaInHa} ha (${areaInSkm} km²)`
      } else {
        const lengthInM = length(feature, { units: 'meters' })
        const lengthInKm = (lengthInM / 1000).toFixed(2)
        areaText = `${lengthInKm} km`
      }
    }

    const index = searchFeatures.value.findIndex(f => f.id === feature.id)
    if (index !== -1) {
      searchFeatures.value[index] = {
        ...searchFeatures.value[index],
        geometry: feature.geometry,
        properties: {
          ...searchFeatures.value[index].properties,
          areaText
        }
      }
    }

    // Sync updated geometry back to parent
    if (!props.multiSelect && props.selectedGeometry && feature.id === props.selectedGeometry.id) {
      emit('update:selectedGeometry', {
        ...props.selectedGeometry,
        geometry: feature.geometry,
        properties: {
          ...props.selectedGeometry.properties,
          areaText,
          reshaped: true,
        }
      })
    }
  }
}

const onDrawDelete = (e) => {
  const features = e.features
  for (const feature of features) {
    const index = searchFeatures.value.findIndex(f => f.id === feature.id)
    if (index !== -1) {
      searchFeatures.value.splice(index, 1)
    }
  }

  if (props.multiSelect) {
    // Remove deleted features from selected geometries
    const deletedIds = new Set(features.map(f => f.id))
    const remaining = props.selectedGeometries.filter(f => !deletedIds.has(f.id))
    emit('update:selectedGeometries', remaining)
  } else {
    if (searchFeatures.value.length === 0) {
      emit('update:selectedGeometry', null)
    }
  }
}

const deleteFeature = (feature) => {
  const index = searchFeatures.value.findIndex(f => f === feature);
  if (index !== -1) {
    // Remove the drawn shape from MapboxDraw
    if (draw.value && feature.id) {
      draw.value.delete(feature.id)
    }

    searchFeatures.value.splice(index, 1)

    if (props.multiSelect) {
      // Remove from selected geometries if present
      const current = props.selectedGeometries.filter(f => f.id !== feature.id)
      emit('update:selectedGeometries', current)
    } else {
      emit('update:selectedGeometry', null)
    }
  }
}

const editFeature = (feature) => {
  if (!draw.value || !feature.id) return
  const drawFeature = draw.value.get(feature.id)
  if (!drawFeature) return
  draw.value.changeMode('direct_select', { featureId: feature.id })
  if (feature.geometry && map.value) {
    safeFitBounds(map.value, feature.geometry)
  }
}

watch(() => searchText.value, async (value) => {
  if (searchText.value.length === 0) {
    searchFeatures.value = [];

    if (props.multiSelect) {
      // Restore selected geometries
      for (const geom of props.selectedGeometries) {
        searchFeatures.value.push(geom);
      }
    } else if (props.selectedGeometry) {
      searchFeatures.value.push(props.selectedGeometry);
    } else {
      emit('update:selectedGeometry', null);
    }

    previewGeometry.value = null;
    return;
  }

  await search();
})

// The global map can switch to another tile after mount (e.g. layer creation
// focuses the target map's tile), so track the instance and re-attach handlers.
let listeningMap = null
watch([() => isMapReady.value, () => map.value], ([ready, newMap]) => {
  if (listeningMap && listeningMap !== newMap) {
    listeningMap.off('draw.create', onDrawCreate)
    listeningMap.off('draw.update', onDrawUpdate)
    listeningMap.off('draw.delete', onDrawDelete)
    listeningMap = null
  }

  if (ready && newMap && listeningMap !== newMap) {
    newMap.on('draw.create', onDrawCreate)
    newMap.on('draw.update', onDrawUpdate)
    newMap.on('draw.delete', onDrawDelete)
    listeningMap = newMap

    // Add existing boundary geometry to draw if it was set before map was ready
    if (props.selectedGeometry?.geometry && props.selectedGeometry?.properties?.isBoundary && draw.value) {
      const existingInDraw = draw.value.get(props.selectedGeometry.id)
      if (!existingInDraw) {
        draw.value.add({
          type: 'Feature',
          id: props.selectedGeometry.id,
          geometry: props.selectedGeometry.geometry,
          properties: { color: props.color || '#0059ff' }
        })
        safeFitBounds(map.value, props.selectedGeometry.geometry)
      }
    }
  }
}, { immediate: true })

watch(() => props.color, (newColor) => {
  if (!draw.value || !newColor) return
  const allFeatures = draw.value.getAll()
  for (const feature of allFeatures.features) {
    draw.value.setFeatureProperty(feature.id, 'color', newColor)
  }
})

watch(() => props.selectedGeometry, (newGeometry) => {
  if (!newGeometry) {
    return
  }

  // Check if feature already exists in the list
  const existingIndex = searchFeatures.value.findIndex(f =>
    f.id && newGeometry.id && f.id === newGeometry.id
  )

  const feature = { ...newGeometry }

  // Ensure ID is preserved
  if (!feature.id) {
    feature.id = generateUniqueId('selected')
  }

  if (!feature.properties) {
    feature.properties = {}
  }

  if (!feature.properties.name) {
    feature.properties.name = "Selected Area"
  }

  if (feature.geometry && !feature.geometry.type) {
    feature.geometry.type = "Polygon"
  }

  if (feature.geometry && !feature.properties.areaText) {
    try {
      if (hasAreaGeometry(feature.geometry)) {
        const areaInSm = area(feature)
        const areaInHa = (areaInSm / 10000).toFixed(3)
        const areaInSkm = (areaInSm / 1000000).toFixed(4)
        feature.properties.areaText = `${areaInHa} ha (${areaInSkm} km²)`
      } else if (feature.geometry.type === 'LineString' && isClosedLineString(feature.geometry)) {
        const polygonGeom = closedLineStringToPolygon(feature.geometry)
        const areaInSm = area({ type: 'Feature', geometry: polygonGeom })
        const areaInHa = (areaInSm / 10000).toFixed(3)
        const areaInSkm = (areaInSm / 1000000).toFixed(4)
        feature.properties.areaText = `${areaInHa} ha (${areaInSkm} km²)`
      }
    } catch (error) {
      console.error('Error calculating area:', error)
    }
  }

  // Update in-place if exists, otherwise add to the end
  if (existingIndex !== -1) {
    searchFeatures.value[existingIndex] = feature
  } else {
    searchFeatures.value.push(feature)
  }

  // Add boundary geometry to MapboxDraw for map visibility
  if (feature.geometry && feature.properties?.isBoundary && draw.value && map.value) {
    const existingInDraw = draw.value.get(feature.id)
    if (!existingInDraw) {
      draw.value.add({
        type: 'Feature',
        id: feature.id,
        geometry: feature.geometry,
        properties: { color: props.color || '#0059ff' }
      })
      safeFitBounds(map.value, feature.geometry)
    }
  }
}, { immediate: true })

// Watch for selectedGeometries changes (multi-select mode)
watch(() => props.selectedGeometries, (newGeometries) => {
  if (!newGeometries || newGeometries.length === 0) {
    return
  }

  // Add any new geometries to the search features list
  for (const geom of newGeometries) {
    const existingIndex = searchFeatures.value.findIndex(f =>
      f.id && geom.id && f.id === geom.id
    )

    if (existingIndex === -1) {
      const feature = { ...geom }

      // Ensure ID is preserved
      if (!feature.id) {
        feature.id = generateUniqueId('selected')
      }

      if (!feature.properties) {
        feature.properties = {}
      }

      if (!feature.properties.name) {
        feature.properties.name = "Selected Area"
      }

      if (feature.geometry && !feature.geometry.type) {
        feature.geometry.type = "Polygon"
      }

      if (feature.geometry && !feature.properties.areaText) {
        try {
          if (hasAreaGeometry(feature.geometry)) {
            const areaInSm = area(feature)
            const areaInHa = (areaInSm / 10000).toFixed(3)
            const areaInSkm = (areaInSm / 1000000).toFixed(4)
            feature.properties.areaText = `${areaInHa} ha (${areaInSkm} km²)`
          } else if (feature.geometry.type === 'LineString' && isClosedLineString(feature.geometry)) {
            const polygonGeom = closedLineStringToPolygon(feature.geometry)
            const areaInSm = area({ type: 'Feature', geometry: polygonGeom })
            const areaInHa = (areaInSm / 10000).toFixed(3)
            const areaInSkm = (areaInSm / 1000000).toFixed(4)
            feature.properties.areaText = `${areaInHa} ha (${areaInSkm} km²)`
          }
        } catch (error) {
          console.error('Error calculating area:', error)
        }
      }

      searchFeatures.value.push(feature)
    }
  }
}, { immediate: true, deep: true })

onBeforeUnmount(() => {
  if (listeningMap) {
    listeningMap.off('draw.create', onDrawCreate)
    listeningMap.off('draw.update', onDrawUpdate)
    listeningMap.off('draw.delete', onDrawDelete)
    listeningMap = null
  }
  // The draw control keeps its object identity after being removed from the map,
  // but its internal store is gone — calling into it then throws mid-unmount and
  // leaves the component tree half torn down.
  try {
    // Clean up area label layers/sources added by DialogCreate
    if (draw.value && map.value) {
      const allFeatures = draw.value.getAll()
      for (const feature of allFeatures.features) {
        const sourceId = 'areaLabelSource-' + feature.id
        const layerId = 'areaLabel-' + feature.id
        if (map.value.getLayer(layerId)) {
          map.value.removeLayer(layerId)
        }
        if (map.value.getSource(sourceId)) {
          map.value.removeSource(sourceId)
        }
      }
    }
    // Clean up drawn features from the map
    if (draw.value) {
      draw.value.deleteAll()
    }
  } catch (error) {
    console.warn('Draw cleanup skipped, control already detached', error)
  }
  // Don't reset selected geometry on unmount
  searchFeatures.value = []
  previewGeometry.value = null
})
</script>

<template>
  <div>
    <div class="relative">
      <Input
        placeholder="Search for country, city or place"
        v-model="searchText"
      />
      <Button
        v-if="searchText.length > 0 && !isSearching"
        variant="ghost"
        class="p-0 w-6 h-6 absolute top-2 right-2"
        @click="searchText = ''"
      >
        <X class="w-4 h-4 text-muted-foreground" />
      </Button>
      <Spinner v-if="isSearching" class="w-4 h-4 absolute top-3 right-3" />
    </div>

    <div class="flex items-center">
      <div class="flex justify-center mt-2">
        <Button @click="handleFileInputClick" size="sm" variant="secondary" type="button">
          <FilePlus2 class="w-4 h-4 mr-2" />
          <span>Open</span>
        </Button>
        <input
          ref="featureFileInput"
          type="file"
          multiple
          :accept="IMPORT_ACCEPT"
          @change="handleFile($event)"
          @click="resetFiles()"
          style="visibility: hidden; position: absolute; top: 0; left: 0; pointer-events: none;"
        />
      </div>

      <div class="flex justify-center mt-2">
        <!-- Single allowed mode: toggle button -->
        <template v-if="allowedDrawModes.length === 1">
          <Button
            v-if="!isDrawing"
            @click="startDrawing(drawModeMap[allowedDrawModes[0]].mode)"
            size="sm"
            variant="secondary"
            type="button"
            class="ml-2"
          >
            <component :is="drawModeMap[allowedDrawModes[0]].icon" class="w-4 h-4 mr-2" />
            <span>Draw</span>
          </Button>
          <Button
            v-else
            @click="stopDrawing"
            size="sm"
            variant="destructive"
            type="button"
            class="ml-2"
          >
            <Square class="w-4 h-4 mr-2" />
            <span>Stop</span>
          </Button>
        </template>

        <!-- Multiple allowed modes: popover or stop button -->
        <template v-else>
          <Button
            v-if="isDrawing"
            @click="stopDrawing"
            size="sm"
            variant="destructive"
            type="button"
            class="ml-2"
          >
            <Square class="w-4 h-4 mr-2" />
            <span>Stop drawing</span>
          </Button>
          <Popover v-else v-model:open="drawPopoverOpen">
            <PopoverTrigger as-child>
              <Button size="sm" variant="secondary" type="button" class="ml-2">
                <Brush class="w-4 h-4 mr-2" />
                <span>Draw</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-auto p-2" align="start">
              <div class="flex gap-1">
                <Button
                  v-for="modeKey in allowedDrawModes"
                  :key="modeKey"
                  size="sm"
                  variant="ghost"
                  type="button"
                  @click="startDrawing(drawModeMap[modeKey].mode); drawPopoverOpen = false"
                  class="flex flex-col items-center gap-1 h-auto py-2 px-3"
                >
                  <component :is="drawModeMap[modeKey].icon" class="w-5 h-5" />
                  <span class="text-xs">{{ drawModeMap[modeKey].label }}</span>
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </template>
      </div>
    </div>

    <div v-if="searchFeatures.length > 0" class="mt-2">
      <div class="bg-background border border-border rounded-md overflow-hidden">
        <FeatureSelectionItem
          v-for="(feature, index) in searchFeatures"
          :key="feature.id || index"
          :feature="feature"
          :index="index"
          :map="map"
          :is-selected="multiSelect
            ? selectedGeometries.some(g => g.id === feature.id)
            : (selectedGeometry && feature.id && selectedGeometry.id === feature.id)"
          :multi-select="multiSelect"
          :default-preview="defaultPreview"
          @select="selectGeometry"
          @toggle="toggleGeometrySelection"
          @delete="deleteFeature"
          @edit="editFeature"
        />
      </div>
    </div>
  </div>
</template>
